import React, {
  useEffect,
  useMemo,
  useState,
  useContext,
  useCallback,
} from 'react';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import MessagingContext, { MessagingPortfolioProps } from './Messaging.context';
import useMatchMedia from 'hooks/useMatchMedia';
import { SessionType } from 'models/Message';
import { useParams } from 'react-router-dom';
import api from 'api';
import { focusElement } from '../../pages/Chat/helpers';
import { getQASide } from 'utils';

type MessagingProviderType = {
  sessionId?: string;
  messagingPortfolioProps?: MessagingPortfolioProps;
};

const Messaging: React.FC<MessagingProviderType> = (props) => {
  const {
    children,
    sessionId: propsSessionId,
    messagingPortfolioProps,
  } = props;

  const { sessionId: paramsSessionId } = useParams<{ sessionId: string }>();

  const sessionId = useMemo(
    () => paramsSessionId || propsSessionId,
    [paramsSessionId, propsSessionId],
  );

  const [contactOpen, setContactOpen] = useState<boolean>(false);
  const [contactsOpen, setContactsOpen] = useState<boolean>(false);

  const [loadingSession, setLoadingSession] = useState<boolean>(false);
  const [currentSession, setCurrentSession] = useState<SessionType | null>();

  const { popServerError } = useContext(PopNotificationsContext);

  const userSide = useMemo(
    () =>
      !currentSession
        ? null
        : getQASide(!currentSession.isRepresentingSellSide),
    [currentSession],
  );

  const isPhablet = useMatchMedia('isPhablet');

  const updateCurrentSession = useCallback(
    (updatedSession: Partial<SessionType>) =>
      setCurrentSession((prev) => ({ ...prev, ...updatedSession })),
    [],
  );

  useEffect(() => {
    setContactOpen(!isPhablet);
    setContactsOpen(!isPhablet);
  }, [isPhablet]);

  useEffect(() => {
    if (!sessionId) return;

    (async () => {
      try {
        setLoadingSession(true);
        setCurrentSession(null);
        const { data } = await api.messages.getSessionById(sessionId);
        setCurrentSession(data);
        focusElement('#message-form-input');
      } catch (e) {
        popServerError(e);
      } finally {
        setLoadingSession(false);
      }
    })();
  }, [popServerError, sessionId]);

  return (
    <MessagingContext.Provider
      value={{
        contactOpen,
        contactsOpen,
        sessionId,
        loadingSession,
        currentSession,
        messagingPortfolioProps,
        userSide,
        setContactOpen,
        setContactsOpen,
        updateCurrentSession,
      }}
    >
      {children}
    </MessagingContext.Provider>
  );
};

export default Messaging;
